import {
    ChartSettingsT,
    CustomizableChartSettings,
    OrderedColumnsT,
    TimeScales as TS,
    VizType,
} from 'business/definitions';
import { isDateOrYear, isTypeNumeric } from 'helpers/helpers';

import { StaticSettings } from './definitions';
import some from 'lodash/some';

/**
 * Return true if the given setting should be disabled based on the props.
 * @param  {String}  settingName Setting name
 * @param  {VizType}  vizType Viz type of the chart component
 * @param  {ChartSettingsT} settings Current chart's settings
 * @param  {ExploreMetricT[]} sentenceMetrics List of the chart metrics
 * @param  {ExploreDimensionT[]} sentenceDimensions List of the chart dimensions
 * @return {Boolean}
 */
export function isSettingDisabled(
    settingName: string,
    vizType: VizType,
    settings: ChartSettingsT,
    sentenceMetrics: ExploreMetricT[],
    sentenceDimensions: ExploreDimensionT[],
    chartDimensions?: OrderedColumnsT,
): boolean | string | void | number {
    // TODO: improve typing
    const isZKeyProbable =
        sentenceDimensions.length > 1 ||
        (sentenceDimensions.length > 0 && sentenceMetrics.length > 1) ||
        (settings && settings.isYearlyComparison);

    const xType = chartDimensions?.x?.type;

    const blockStackSecondaryAxis =
        settings.secondaryYAxis && sentenceMetrics.length <= 2; //Stacking doesn't make sense when using 2 metrics and a secondaryAxis

    switch (settingName) {
        case 'isNormalized':
        case 'isStackedBars':
            return !isZKeyProbable || blockStackSecondaryAxis;
        case 'areLabelsShownOutside':
            return !settings.areLabelsShown;
        case 'showLegendOnRightSide':
            return !settings.showLegend;
        case 'maxValue':
            return vizType === 'gauge' && settings.maxValueField;
        case 'maxValueField':
            return vizType === 'gauge' && settings.maxValue;
        case 'pivotField':
            return settings.isYearlyComparison || vizType === 'pivot_table';
        case 'isYearlyComparison':
            /* We disable isYearlyComparison if there is more than one dim
             * or if the dim is not a date with a monthly, daily or quarterly scale */
            return (
                sentenceDimensions.filter(
                    (dim) =>
                        dim.scale &&
                        [TS.month, TS.quarter, TS.day].includes(dim.scale),
                ).length !== 1
            );
        case 'secondaryYAxis':
            return (
                sentenceDimensions.length !== 1 ||
                sentenceMetrics.length <= 1 ||
                settings.isYearlyComparison
            );
        case 'areLabelsShownInside':
            return !settings.areLabelsShown || settings.isStackedBars;
        case 'areLabelsVertical':
            return vizType === 'horizontal_bar' || !settings.areLabelsShown;
        case 'isEvolutionDisplayed':
            return !settings.areLabelsShown || settings.isYearlyComparison;
        case 'reverseGroupBy':
            return false;
        case 'useGradientColor':
            return Boolean(chartDimensions?.z);
        case 'sortDimension':
            if (chartDimensions === undefined) return false;
            return (
                !xType ||
                isDateOrYear(xType) ||
                isTypeNumeric(xType) ||
                (!settings.isStackedBars &&
                    (sentenceMetrics.length > 1 || Boolean(chartDimensions?.z)))
            );
        default:
            return false;
    }
}

/** Regexp to identify OrgChart tree dimensions.
 */
const ORG_CHART_TREE = /org_chart.*.tree$/;

/**
 * Return true if the given setting should be disabled based on the props.
 * @param  {String}  settingName Setting name
 * @param  {VizType}  vizType Viz type of the chart component
 * @param  {ExploreDimensionT[]} sentenceDimensions List of the query dimensions
 * @param  {ChartSettingsT} currentSettings Current chart's settings
 */
export function isSettingInvisible(
    settingName: string,
    vizType: VizType,
    sentenceDimensions: ExploreDimensionT[],
    currentSettings: ChartSettingsT,
): boolean {
    if (
        settingName.startsWith('metricsCumulated.') ||
        ['pageSize', 'rowHeight'].includes(settingName)
    ) {
        return vizType === 'pivot_table';
    }

    if (settingName === 'isEvolutionDisplayed') {
        return (
            sentenceDimensions.filter(
                (d) => d.type && ['date', 'datetime', 'year'].includes(d.type),
            ).length !== 1
        );
    }

    if (settingName === 'reverseGradientColor') {
        return !currentSettings.useGradientColor;
    }

    if (settingName === 'customPalette') {
        return !!currentSettings.useGradientColor;
    }

    if (settingName === 'pivotField') {
        const hasOrgChart = some(sentenceDimensions, (dim) =>
            ORG_CHART_TREE.test(dim.field),
        );
        return hasOrgChart || vizType === 'pivot_table';
    }
    return false;
}

/**
 * Get new settings based on payload from the form.
 * The cumulated metrics have a special behaviour, with metricsCumulated at the
 * beginning of the key, which needs to be removed.
 */
export function getSettingsDict(settings: ChartSettingsT): ChartSettingsT {
    // TODO: improve typing
    const hasMetricsCumulated =
        Object.keys(settings).some((key) =>
            key.startsWith('metricsCumulated.'),
        ) || settings.metricsCumulated;
    const newSettings = { ...settings };
    const metricsCumulated = settings.metricsCumulated
        ? [...settings.metricsCumulated]
        : [];

    Object.keys(settings).forEach((key) => {
        if (key.startsWith('metricsCumulated.')) {
            const value = settings[key];
            delete newSettings[key];
            const metricName = key.replace('metricsCumulated.', '');
            if (value && !metricsCumulated.includes(metricName)) {
                metricsCumulated.push(metricName);
            } else if (!value && metricsCumulated.includes(metricName)) {
                metricsCumulated.splice(
                    metricsCumulated.indexOf(metricName),
                    1,
                );
            }
        }
        if (key === 'secondaryYAxis') {
            const hasSecondaryYAxis = Boolean(settings[key]);
            newSettings.reverseGroupBy =
                newSettings.reverseGroupBy || hasSecondaryYAxis;
        }
    });
    // NOTE: if the payload do not contain any metricsCumulated key,
    // this means no update has been made on this particular setting and we don't
    // want to return an empty array, else it will reset the setting
    if (!hasMetricsCumulated) {
        return newSettings;
    }
    return { ...newSettings, metricsCumulated };
}

export function isFractionThresholdSetting(
    setting: keyof CustomizableChartSettings | StaticSettings,
    yType: DataTypesT | undefined,
) {
    return (
        (setting === 'maxValue' || setting === 'minValue') &&
        yType === 'fraction'
    );
}
